import React, { useState, useRef } from 'react';
import { Button, MarkdownRenderer } from '../common';
import { useUploadMedia } from '../../hooks/useSubmissions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/x-m4a'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

export const GMPromptEditor = ({
  gameId,
  initialPrompt = {},
  passageLength = 2,
  onPassageLengthChange,
  onSubmit,
  loading = false,
  canEditLength = true,
  roundNumber = 1,
  showPassageFields = true,
  initialPassageTitle = '',
  initialPassageDescription = '',
}) => {
  const fileInputRef = useRef(null);
  const uploadMedia = useUploadMedia(gameId);

  const [prompt, setPrompt] = useState({
    content: initialPrompt.content || '',
    mediaUrl: initialPrompt.mediaUrl || '',
    mediaType: initialPrompt.mediaType || 'text',
  });
  const [passageTitle, setPassageTitle] = useState(initialPassageTitle);
  const [passageDescription, setPassageDescription] = useState(initialPassageDescription);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(prompt, {
      passageTitle: passageTitle.trim(),
      passageDescription: passageDescription.trim(),
    });
  };

  const handleMediaTypeSelect = (type) => {
    setPrompt({ ...prompt, mediaType: type, mediaUrl: '' });
    setUploadedFile(null);
    setUploadError(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError(null);

    const allowedTypes = prompt.mediaType === 'image' ? ALLOWED_IMAGE_TYPES : ALLOWED_AUDIO_TYPES;
    const maxSize = prompt.mediaType === 'image' ? MAX_IMAGE_SIZE : MAX_AUDIO_SIZE;

    // Validate type
    if (!allowedTypes.includes(file.type)) {
      setUploadError(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    // Validate size
    if (file.size > maxSize) {
      setUploadError(`File too large. Maximum: ${maxSize / 1024 / 1024}MB`);
      return;
    }

    try {
      const result = await uploadMedia.mutateAsync({ file, type: prompt.mediaType });
      setUploadedFile({
        name: file.name,
        url: result.media.url,
      });
      setPrompt({ ...prompt, mediaUrl: result.media.url });
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      console.error('Upload failed:', err);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveMedia = () => {
    setUploadedFile(null);
    setPrompt({ ...prompt, mediaUrl: '' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {canEditLength && (
        <div className="bg-gray-50 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Passage Length (L)
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="1"
              max="5"
              value={passageLength}
              onChange={(e) => onPassageLengthChange(parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-2xl font-bold text-primary-600 w-8 text-center">
              {passageLength}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Cost: {passageLength} influence will be added to the pot
          </p>
        </div>
      )}

      {showPassageFields && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Passage Title <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={passageTitle}
              onChange={(e) => setPassageTitle(e.target.value)}
              placeholder="An (optional) title for this passage"
              maxLength={200}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Passage Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={passageDescription}
              onChange={(e) => setPassageDescription(e.target.value)}
              placeholder="An (optional) description for this passage"
              maxLength={500}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Your Prompt
          </label>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
            <button
              type="button"
              className={`px-3 py-1 transition-colors ${
                !previewMode
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setPreviewMode(false)}
            >
              Write
            </button>
            <button
              type="button"
              className={`px-3 py-1 transition-colors ${
                previewMode
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setPreviewMode(true)}
            >
              Preview
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Supports <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Markdown</a>
        </p>
        {previewMode ? (
          <div className="w-full min-h-[200px] px-4 py-3 rounded-lg border border-gray-300 bg-gray-50 overflow-auto">
            {prompt.content ? (
              <MarkdownRenderer content={prompt.content} />
            ) : (
              <p className="text-gray-400 italic">Nothing to preview yet...</p>
            )}
          </div>
        ) : (
          <textarea
            value={prompt.content}
            onChange={(e) => setPrompt({ ...prompt, content: e.target.value })}
            placeholder="Set the stage for this round. Describe the situation, assert details (up to L), and prompt action from your players..."
            rows={8}
            maxLength={5000}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
            required
          />
        )}
        <div className="flex justify-between text-sm text-gray-500 mt-1">
          <span>Set the scene and prompt your players</span>
          <span>{prompt.content.length}/5000</span>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Attach Media (optional)</h4>
        <div className="flex gap-4">
          <button
            type="button"
            className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
              prompt.mediaType === 'image'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMediaTypeSelect('image')}
          >
            <span className="block text-2xl mb-1">🖼️</span>
            <span className="text-sm">Image</span>
          </button>
          <button
            type="button"
            className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
              prompt.mediaType === 'audio'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMediaTypeSelect('audio')}
          >
            <span className="block text-2xl mb-1">🎵</span>
            <span className="text-sm">Audio</span>
          </button>
        </div>

        {(prompt.mediaType === 'image' || prompt.mediaType === 'audio') && (
          <div className="mt-3 space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={prompt.mediaType === 'image'
                ? ALLOWED_IMAGE_TYPES.join(',')
                : ALLOWED_AUDIO_TYPES.join(',')}
              onChange={handleFileSelect}
              disabled={uploadMedia.isPending}
              className="hidden"
            />

            {uploadedFile ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {prompt.mediaType === 'image' ? (
                  <img
                    src={getMediaUrl(uploadedFile.url)}
                    alt={uploadedFile.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                  <p className="text-xs text-green-600">Uploaded successfully</p>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveMedia}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMedia.isPending}
                loading={uploadMedia.isPending}
              >
                {uploadMedia.isPending ? 'Uploading...' : `Upload ${prompt.mediaType === 'image' ? 'Image' : 'Audio'}`}
              </Button>
            )}

            {uploadError && (
              <p className="text-sm text-red-600">{uploadError}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4">
        <Button type="submit" loading={loading}>
          Post Next Prompt
        </Button>
      </div>
    </form>
  );
};
